import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { markPromotedToAdmin } from "@/lib/rolePromotion";
import { useToast } from "@/hooks/use-toast";
import { orgAdminClaimErrorMessage } from "@/lib/orgInvitations";
import { ADMIN_HOME } from "@/lib/postLoginDestination";
import { clearCapturedOrgInvite, readCapturedOrgInvite } from "@/lib/orgInviteBootstrap";

/**
 * Redeems the memory-held org-admin invitation for an ALREADY-SIGNED-IN
 * visitor. Shared by the homepage (legacy links) and /activate (Slice 5
 * links): AuthModal only runs for signed-out visitors, so without this a
 * signed-in invitee's token was silently dropped. The RPC is email-bound and
 * idempotent for the same user, so a re-fire is safe.
 *
 * A bound (pre-activation) claim attaches the account to an existing org →
 * ADMIN_HOME to review it; a legacy invite has no org yet → /admin/setup. The
 * claim result's org_id decides. On error the token stays in memory so the
 * visitor can retry after, e.g., switching accounts.
 *
 * An in-flight guard makes a double-fire (double-click, a re-render re-running
 * an effect) send exactly one RPC rather than racing two claims; `isRedeeming`
 * is exposed so a calling button can disable itself while a claim is pending.
 */
export function useRedeemCapturedOrgInvite() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const inFlight = useRef(false);
  const [isRedeeming, setIsRedeeming] = useState(false);

  const redeem = useCallback(async () => {
    const token = readCapturedOrgInvite();
    if (!token) return;
    if (inFlight.current) return;
    inFlight.current = true;
    setIsRedeeming(true);
    try {
      // supabase-js resolves with `{ error }` for a Postgres-level rejection
      // (RAISE in the RPC) but REJECTS the promise on a transport failure
      // (offline, DNS, an aborted fetch). acceptOrgInvitation in
      // src/lib/orgInvitations.ts normalizes the same hazard; without this,
      // an unhandled rejection here would skip the toast entirely and leave
      // the token stuck with no visible feedback.
      let data: unknown = null;
      let error: { message?: string } | null = null;
      try {
        ({ data, error } = await supabase.rpc("claim_org_admin_role", { p_token: token }));
      } catch (err) {
        // A rejected promise is a transport failure, not a database verdict,
        // so route it through the same mapped copy.
        error = { message: err instanceof Error ? err.message : String(err ?? "") };
      }
      if (error) {
        const claimMessage = orgAdminClaimErrorMessage(error.message ?? "");
        toast({
          title: claimMessage.title,
          description: claimMessage.description,
          variant: "destructive",
        });
        return;
      }
      markPromotedToAdmin(queryClient);
      clearCapturedOrgInvite();
      const boundOrgId = (data as { org_id?: string | null } | null)?.org_id ?? null;
      navigate(boundOrgId ? ADMIN_HOME : "/admin/setup", { replace: true });
    } finally {
      inFlight.current = false;
      setIsRedeeming(false);
    }
  }, [navigate, queryClient, toast]);

  return useMemo(() => ({ redeem, isRedeeming }), [redeem, isRedeeming]);
}
